import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Modal as GarnishModal, useResponsive } from '@sg/garnish';

import { useTrackEffect } from '@order/Telemetry';

import {
  useDeletePaymentMethod,
  usePaymentMethods,
  useUpdatePaymentMethod,
} from '../hooks';
import { CreditCardFormEdit } from './CreditCardFormEdit';

export const CreditCardFormEditModal = (
  props: CreditCardFormEditModalProps,
) => {
  const {
    visible,
    paymentMethodId,
    Modal: CustomModal,
    onRequestClose,
  } = props;

  useTrackEffect('payment_credit.view_card');
  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  const { paymentMethods } = usePaymentMethods();

  const paymentMethod = paymentMethods?.find(
    (item) => item.id === paymentMethodId,
  );

  const paymentMethodType =
    paymentMethod?.__typename === 'PaymentMethodCard'
      ? 'PAYMENT_METHOD_CARD'
      : 'BILLING_ACCOUNT';

  const { updatePaymentMethod, isUpdating } = useUpdatePaymentMethod({
    onSuccess: onRequestClose,
  });

  const { deletePaymentMethod, isDeleting } = useDeletePaymentMethod({
    onSuccess: onRequestClose,
  });

  const handleDeletePaymentMethod = useCallback(async () => {
    if (!paymentMethod) return;

    return deletePaymentMethod(paymentMethod);
  }, [deletePaymentMethod, paymentMethod]);

  if (!paymentMethod) {
    return null;
  }

  // Use the custom Modal if provided, otherwise use the default Modal from garnish
  const Modal = CustomModal ?? GarnishModal;

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={formatMessage(messages.creditCardFormEdit)}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={onRequestClose}
    >
      <Modal.Header onClose={onRequestClose}>
        {formatMessage(messages.creditCardFormEdit)}
      </Modal.Header>

      <SafeAreaView edges={['bottom']} style={styles.container}>
        <CreditCardFormEdit
          paymentMethodId={paymentMethod?.id}
          paymentMethodType={paymentMethodType}
          cardType={paymentMethod?.cardType}
          description={paymentMethod?.description ?? ''}
          nickname={paymentMethod?.nickname ?? ''}
          isDefault={paymentMethod?.isDefault}
          isSaving={isUpdating}
          isDeleting={isDeleting}
          onEdit={updatePaymentMethod}
          onDelete={handleDeletePaymentMethod}
        />
      </SafeAreaView>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormEdit: {
    defaultMessage: 'Edit card',
    description: 'Credit Card Form | Edit',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CreditCardFormEditModalProps = Readonly<{
  paymentMethodId: string;
  visible: boolean;
  onRequestClose: () => void;

  /**
   * Optional Modal component to use instead of the default Modal from garnish.
   */
  Modal?: typeof GarnishModal;
}>;
